// Need to use the React-specific entry point to import createApi
import type {
  FileResponse,
  GetListResponseOfTaskAssignmentSimpleResponse,
  GetTaskAssignmentListRequest,
  GetTaskAssignmentListRequestV2,
  TaskAssignmentBatchStatusByTaskUpdateRequest,
  TaskAssignmentBatchStatusByUserUpdateRequest,
  TaskAssignmentResponse,
  TaskAssignmentViewResponse,
  TaskParticipationStatistics,
  TaskTypeEnum,
} from 'src/api/ProactApiClient';
import {taskAssignmentApiClient} from 'src/api/ProactApiClientInstances';
import {transitionTaskAssignmentMutationFn} from 'src/app/tasks/offline/mutations';
import type {TransitionTaskAssignmentArgs} from 'src/app/tasks/offline/mutations';
import {
  taskAssignmentDetailsQueryFn,
  taskAssignmentsListQueryFn,
} from 'src/app/tasks/offline/reads';
import {emptySplitApi} from '../api';
import {clientQuery} from '../clientQuery';

// Define a service using a base URL and expected endpoints
export const taskAssignmentApi = emptySplitApi.injectEndpoints({
  endpoints: builder => ({
    getTaskAssignmentListV2: builder.query<
      GetListResponseOfTaskAssignmentSimpleResponse,
      GetTaskAssignmentListRequestV2
    >({
      queryFn: clientQuery(request =>
        taskAssignmentApiClient.getTaskAssignmentsListV2(request)
      ),
      providesTags: ['TaskAssignments'],
    }),
    getTaskAssignmentsList: builder.query<
      GetListResponseOfTaskAssignmentSimpleResponse,
      GetTaskAssignmentListRequest
    >({
      queryFn: taskAssignmentsListQueryFn(request =>
        taskAssignmentApiClient.getTaskAssignmentsList(request)
      ),
      providesTags: ['TaskAssignments'],
    }),
    getTaskAssignmentView: builder.query<
      TaskAssignmentViewResponse,
      GetTaskAssignmentListRequest
    >({
      queryFn: clientQuery(request =>
        taskAssignmentApiClient.getTaskAssignmentView(request)
      ),
      providesTags: ['TaskAssignments'],
    }),
    getTaskAssignmentDetails: builder.query<TaskAssignmentResponse, number>({
      queryFn: taskAssignmentDetailsQueryFn(id =>
        taskAssignmentApiClient.details(id)
      ),
      providesTags: ['TaskAssignments'],
    }),
    getTaskAssignmentsParticipation: builder.query<
      TaskParticipationStatistics,
      {userId: number; taskType: TaskTypeEnum}
    >({
      queryFn: clientQuery(({userId, taskType}) =>
        taskAssignmentApiClient.getTaskAssignmentsParticipation(
          userId,
          taskType
        )
      ),
      providesTags: ['TaskAssignments'],
    }),
    transitionTaskAssignment: builder.mutation<
      TaskAssignmentResponse,
      TransitionTaskAssignmentArgs
    >({
      queryFn: transitionTaskAssignmentMutationFn,
      invalidatesTags: ['TaskAssignments'],
    }),
    postBatchStatusUpdateByUser: builder.mutation<
      FileResponse | null,
      TaskAssignmentBatchStatusByUserUpdateRequest
    >({
      queryFn: clientQuery(request =>
        taskAssignmentApiClient.postBatchStatusUpdateByUser(request)
      ),
      invalidatesTags: ['TaskAssignments'],
    }),
    postBatchStatusUpdateByTask: builder.mutation<
      FileResponse | null,
      TaskAssignmentBatchStatusByTaskUpdateRequest
    >({
      queryFn: clientQuery(request =>
        taskAssignmentApiClient.postBatchStatusUpdateByTask(request)
      ),
      invalidatesTags: ['TaskAssignments'],
    }),
    supervisorApprovalAcknowledgeToolboxTalk: builder.mutation<
      string,
      {taskId: number; userId: number}
    >({
      queryFn: clientQuery(({taskId, userId}) =>
        taskAssignmentApiClient.supervisorApprovalAcknowledgeToolboxTalk(
          taskId,
          userId
        )
      ),
      invalidatesTags: ['TaskAssignments'],
    }),
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetTaskAssignmentListV2Query,
  useGetTaskAssignmentsListQuery,
  useGetTaskAssignmentViewQuery,
  useGetTaskAssignmentDetailsQuery,
  useGetTaskAssignmentsParticipationQuery,
  useTransitionTaskAssignmentMutation,
  usePostBatchStatusUpdateByUserMutation,
  usePostBatchStatusUpdateByTaskMutation,
  useSupervisorApprovalAcknowledgeToolboxTalkMutation,
} = taskAssignmentApi;
